import { useMemo, useState } from 'react'
import { useParams } from 'react-router'

import {
    Banner,
    Card,
    EditCustomManufacturerForm,
    EditEnphaseForm,
    EditGeneracForm,
    EditSolarEdgeForm,
    IntegrationTutorialPopup,
    PostSubmitPopup,
    Tabs,
    EditTeslaForm
} from '@/components'
import { BannerIdEnum, InverterManufacturerEnum } from '@/enums'
import { Company, InverterManufacturer } from '@/models'
import { feature } from '@/services'

type EditIntegrationsFormProps = {
    company: Company
    onChange: () => void
    basePath?: string
}

const EditIntegrationsForm = ({
    company,
    basePath = '/profile',
    ...props
}: EditIntegrationsFormProps) => {
    const params = useParams()
    const [tab, setTab] = useState<string | number | null>(params.provider || InverterManufacturerEnum.ENPHASE)
    const [isPostSubmitPopupOpen, setIsPostSubmitPopupOpen] = useState(false)

    const tabs = [
        {
            id: InverterManufacturerEnum.ENPHASE,
            title: InverterManufacturer.enphase.title,
            icon: company.enphaseIntegration?.status.icon(),
            to: `${basePath}/integrations/${InverterManufacturerEnum.ENPHASE}`,
            form: <EditEnphaseForm
                company={company}
                integration={company.enphaseIntegration}
                onChange={props.onChange}
                onAdded={() => setIsPostSubmitPopupOpen(true)}
            />
        },
        {
            id: InverterManufacturerEnum.SOLAREDGE,
            title: InverterManufacturer.solaredge.title,
            icon: company.solarEdgeIntegration?.status.icon(),
            to: `${basePath}/integrations/${InverterManufacturerEnum.SOLAREDGE}`,
            form: <EditSolarEdgeForm
                company={company}
                integration={company.solarEdgeIntegration}
                onChange={props.onChange}
                onAdded={() => setIsPostSubmitPopupOpen(true)}
            />
        },
        {
            id: InverterManufacturerEnum.GENERAC,
            title: InverterManufacturer.generac.title,
            icon: company.generacIntegration?.status.icon(),
            to: `${basePath}/integrations/${InverterManufacturerEnum.GENERAC}`,
            form: <EditGeneracForm
                company={company}
                integration={company.generacIntegration}
                onChange={props.onChange}
                onAdded={() => setIsPostSubmitPopupOpen(true)}
            />
        },
        ...feature('tesla-integration') ? [{
            id: InverterManufacturerEnum.TESLA,
            title: InverterManufacturer.tesla.title,
            icon: company.teslaIntegration?.status.icon(),
            to: `${basePath}/integrations/${InverterManufacturerEnum.TESLA}`,
            form: <EditTeslaForm
                company={company}
                integration={company.teslaIntegration}
                onChange={props.onChange}
                onAdded={() => setIsPostSubmitPopupOpen(true)}
            />
        }] : []
    ]

    const activeTab = useMemo(() =>
        tabs.find(item => item.id === tab) || tabs[0]
    , [tab, company])

    return <div className="company-integrations">
        <Banner
            allowedBanners={[BannerIdEnum.TESLA_ANNOUNCEMENT_C]}
            className="mb-4 lg:mb-6"
        />

        <div className="flex flex-wrap gap-2 justify-between">
            <div className="flex flex-col gap-1 max-w-full">
                <h3 className="text-lg">Select Manufacturer to Enter Monitoring Credentials</h3>
                <p className="text-gray-750 text-xs italic w-180 max-w-full">
                    Your privacy and security are our top priority. Your monitoring credentials will be used exclusively
                    to gather and archive data pertaining to systems covered by verified Solar Insure warranties.
                </p>
            </div>

            <div className="flex items-center mb-4 text-sm font-normal">
                {company.monitoringStatus.getBadge()}
                <div className="ml-2">
                    {company.monitoringStatus.title}
                    {company.monitoringStatus.subTitle &&
                        <p className="italic text-xs text-gray-500">{company.monitoringStatus.subTitle}</p>}
                </div>
            </div>
        </div>

        <div className="mt-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                <h3 className="text-lg">
                    Supported Manufacturer Integrations:
                </h3>
                <IntegrationTutorialPopup/>
            </div>

            <Tabs
                className="mt-4"
                design="buttons-secondary"
                tabs={tabs}
                value={tab}
                onChange={setTab}
            />
        </div>

        <Card className="mt-8">
            <h3 className="card-title text-base mb-6">
                {activeTab.title}
            </h3>

            {activeTab.form}
        </Card>
        <EditCustomManufacturerForm
            className="mt-4"
            company={company}
            onChange={props.onChange}
        />

        <PostSubmitPopup isOpen={isPostSubmitPopupOpen} onClose={() => setIsPostSubmitPopupOpen(false)}/>
    </div>
}

export default EditIntegrationsForm
